import { motion } from "framer-motion";
import {
  Search,
  Radar,
  ShieldCheck,
  Handshake,
  Rocket,
} from "lucide-react";

const STEPS = [
  {
    icon: Search,
    title: "Understand Your Business",
    desc: "Deep discovery of your stage, stack, culture & commercial goals.",
  },
  {
    icon: Radar,
    title: "Target & Headhunt Talent",
    desc: "Proactive mapping of the top 3% within your competitor universe.",
  },
  {
    icon: ShieldCheck,
    title: "Vet & Shortlist",
    desc: "Rigorous screening — performance, motivation, references, fit.",
  },
  {
    icon: Handshake,
    title: "Interview & Close",
    desc: "Process design, offer strategy and counter‑offer defense.",
  },
  {
    icon: Rocket,
    title: "Onboard & Scale",
    desc: "Post‑placement care, 90‑day check‑ins and follow‑on hires.",
  },
];

export default function Process() {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative py-20 sm:py-24 lg:py-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#D9F99D]/90">
              Process
            </div>
            <h2
              data-testid="process-heading"
              className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white"
            >
              How We <span className="zx-hi">Work</span>
            </h2>
          </div>
          <p className="max-w-md text-neutral-200">
            A precise, 5‑stage delivery model. No noise. No filler. Just a
            measured path from brief to business impact.
          </p>
        </div>

        {/* Horizontal timeline — desktop */}
        <div className="relative hidden lg:block">
          {/* base line */}
          <div className="absolute left-0 right-0 top-14 h-px bg-white/5" />
          {/* animated progress pulse */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.8, ease: "easeInOut" }}
            style={{ transformOrigin: "left" }}
            className="absolute left-0 right-0 top-14 h-px bg-gradient-to-r from-[#D9F99D] via-[#D9F99D]/80 to-transparent shadow-[0_0_12px_rgba(217,249,157,0.6)]"
          />
          <div className="grid grid-cols-5 gap-4 relative">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.18 * i }}
                data-testid={`process-step-${i}`}
                className="relative group"
              >
                <div className="mx-auto h-28 w-28 rounded-full bg-[#0B0B0B] border border-white/10 flex items-center justify-center relative transition-all duration-500 group-hover:border-[#D9F99D]/60 group-hover:scale-105">
                  <div className="absolute inset-0 rounded-full border border-[#D9F99D]/30 opacity-0 group-hover:opacity-100 transition-opacity zx-pulse" />
                  <s.icon size={30} className="text-[#D9F99D] transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute -top-1 -right-1 h-8 w-8 rounded-full bg-[#D9F99D] text-black text-xs font-mono font-semibold flex items-center justify-center shadow-[0_0_18px_rgba(217,249,157,0.65)]">
                    0{i + 1}
                  </div>
                </div>
                <div className="mt-7 text-center px-2">
                  <h3 className="font-display text-lg text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-neutral-200 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Vertical timeline — mobile/tablet */}
        <div className="lg:hidden space-y-6 relative">
          <div className="absolute left-[31px] top-4 bottom-4 w-px bg-gradient-to-b from-[#D9F99D]/60 via-white/10 to-transparent" />
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex gap-5 relative"
              data-testid={`process-step-mobile-${i}`}
            >
              <div className="h-[62px] w-[62px] shrink-0 rounded-full bg-[#0E0E10] border border-white/10 flex items-center justify-center relative">
                <s.icon size={22} className="text-[#D9F99D]" />
                <div className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-[#D9F99D] text-black text-[10px] font-mono flex items-center justify-center">
                  0{i + 1}
                </div>
              </div>
              <div className="pt-1">
                <h3 className="font-display text-lg text-white">{s.title}</h3>
                <p className="mt-1.5 text-sm text-neutral-200 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
